import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import DataObjectIcon from '@mui/icons-material/DataObject';
import BarChartIcon from '@mui/icons-material/BarChart';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius
}));

const RequestSummary = ({ 
  cohort, 
  variables, 
  analysisPlan, 
  onSaveRequest, 
  onSubmitRequest,
  activeStep,
  steps
}) => {
  const [requestName, setRequestName] = React.useState('');
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  // Automatisch einen Anfragenamen generieren
  React.useEffect(() => {
    if (cohort && analysisPlan) {
      setRequestName(`${cohort.name} - ${analysisPlan.planTitle} Analyse`);
    }
  }, [cohort, analysisPlan]);

  const handleSaveRequest = () => {
    if (!cohort || !variables || !analysisPlan) {
      setError('Bitte vervollständigen Sie alle vorherigen Schritte, bevor Sie die Anfrage speichern.');
      return;
    }

    const request = {
      name: requestName,
      cohort,
      variables,
      analysisPlan,
      status: 'saved',
      createdAt: new Date().toISOString()
    };

    onSaveRequest(request);
    setSuccess('Ihre Anfrage wurde erfolgreich gespeichert.');
    setError('');
  };

  const handleSubmitRequest = () => {
    if (!cohort || !variables || !analysisPlan) {
      setError('Bitte vervollständigen Sie alle vorherigen Schritte, bevor Sie die Anfrage einreichen.');
      return;
    }

    const request = {
      name: requestName,
      cohort,
      variables,
      analysisPlan,
      status: 'submitted',
      createdAt: new Date().toISOString()
    };

    onSubmitRequest(request);
    setSuccess('Ihre Anfrage wurde erfolgreich eingereicht.');
    setError('');
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
        Zusammenfassung und Einreichung
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Überprüfen Sie Ihre Datenanfrage und reichen Sie sie ein oder speichern Sie sie für später.
      </Typography>
      
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Anfrage-Zusammenfassung
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Name der Anfrage
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {requestName}
                  </Typography>
                  <Chip 
                    label="Entwurf" 
                    color="primary" 
                    variant="outlined" 
                    size="small" 
                  />
                </Paper>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon color="primary" fontSize="small" />
                Schritt 1: Kohortendefinition
              </Typography>
              
              {cohort ? (
                <StyledPaper>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {cohort.name}
                    </Typography>
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label="Definiert" 
                      color="success" 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {cohort.conditionString}
                  </Typography>
                </StyledPaper>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Kohortendefinition fehlt. Bitte kehren Sie zu Schritt 1 zurück.
                </Alert>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DataObjectIcon color="primary" fontSize="small" />
                Schritt 2: Datenvariablen
              </Typography>
              
              {variables ? (
                <StyledPaper>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {variables.name}
                    </Typography>
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label={`${variables.variables.length} Variablen`} 
                      color="success" 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {variables.variables.slice(0, 10).map((variable, index) => (
                      <Chip 
                        key={index} 
                        label={variable.id} 
                        size="small" 
                        variant="outlined"
                        sx={{ m: 0.5 }}
                      />
                    ))}
                    {variables.variables.length > 10 && (
                      <Chip 
                        label={`+${variables.variables.length - 10} weitere`} 
                        size="small" 
                        variant="outlined"
                        sx={{ m: 0.5 }}
                      />
                    )}
                  </Box>
                </StyledPaper>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Datenvariablen fehlen. Bitte kehren Sie zu Schritt 2 zurück.
                </Alert>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChartIcon color="primary" fontSize="small" />
                Schritt 3: Analyseplan
              </Typography>
              
              {analysisPlan ? (
                <StyledPaper>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {analysisPlan.name}
                    </Typography>
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label={analysisPlan.planTitle} 
                      color="success" 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" paragraph>
                    <strong>Ausgewählte Optionen:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {analysisPlan.options.map((option, index) => (
                      <Chip 
                        key={index} 
                        label={option} 
                        size="small" 
                        variant="outlined"
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                  {analysisPlan.customAnalysis && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Benutzerdefinierte Anweisungen:
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}>
                        "{analysisPlan.customAnalysis}"
                      </Typography>
                    </Box>
                  )}
                </StyledPaper>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Analyseplan fehlt. Bitte kehren Sie zu Schritt 3 zurück.
                </Alert>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}
              
              {success && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  {success}
                </Alert>
              )}
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveRequest}
                >
                  Als Entwurf speichern
                </Button>
                
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SendIcon />}
                  onClick={handleSubmitRequest}
                  disabled={!cohort || !variables || !analysisPlan}
                >
                  Anfrage einreichen
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Nächste Schritte
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Anfrage prüfen" 
                    secondary="Überprüfen Sie alle Details Ihrer Anfrage"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SaveIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Als Entwurf speichern" 
                    secondary="Speichern Sie Ihre Anfrage, um sie später zu bearbeiten"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SendIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Anfrage einreichen" 
                    secondary="Reichen Sie Ihre Anfrage zur Bearbeitung ein"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DownloadIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Ergebnisse erhalten" 
                    secondary="Nach Bearbeitung erhalten Sie die Analyseergebnisse"
                  />
                </ListItem>
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Hinweise
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Bearbeitungszeit:</strong> Die Bearbeitung Ihrer Anfrage dauert in der Regel 3-5 Werktage.
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Vertraulichkeit:</strong> Alle Daten werden gemäß unseren Datenschutzrichtlinien vertraulich behandelt.
              </Typography>
              
              <Typography variant="body2">
                <strong>Unterstützung:</strong> Bei Fragen kontaktieren Sie bitte unseren Support unter support@argus-health.com.
              </Typography>
            </CardContent>
          </Card>
          
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Partnerkliniken
              </Typography>
              
              <Typography variant="body2" paragraph>
                Ihre Anfrage wird an folgende Partnerkliniken weitergeleitet:
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <PeopleIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText primary="Universitätsklinikum München" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PeopleIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText primary="Charité Berlin" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PeopleIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText primary="Universitätsklinikum Frankfurt" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RequestSummary;
